import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, DataTableCustomActions, DataTableField } from "../../../../components/data/DataTable";
import DataTableActiveToggle from "../../../../components/data/DataTableActiveToggle";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import Modal from "../../../../components/base/Modal";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { API_ROUTES } from "../../../../core/Constants";
import { isRoot, setPage, useHub } from "../../../../core/Global";
import WaInstance from "../models/WaInstance";
import WaInstanceStateCell from "../components/WaInstanceStateCell";
import WaInstanceForm from "./WaInstanceForm";


export default function WaInstanceList() {

  const hub = useHub();
  const navigate = useNavigate();

  const [formDrawer, formDrawerSet] = useState<boolean>(false);
  const [selectedId, selectedIdSet] = useState<string>();
  const [selectedItem, selectedItemSet] = useState<WaInstance>();
  const [primaryModal, primaryModalSet] = useState<boolean>(false);
  const [refresh, refreshSet] = useState<boolean>(false);

  useEffect(() => {
    // The rows carry the Green API tokens, so this page is root-only on the client too --
    // the controller refuses anyone else regardless, this just avoids an empty error page.
    if (!isRoot()) {
      navigate("/");
      return;
    }
    setPage("WA Instances", "System");
  }, [])

  useEffect(() => {
    if (refresh) {
      setTimeout(() => {
        refreshSet(false);
      }, 500);
    }
  }, [refresh])

  const NameCol = (data: WaInstance) => (
    <FlexRow>
      <div className="fw-bold">{data.name}</div>
      {
        // Only the primary is marked; a backup is the normal case and needs no badge. A primary
        // that is switched off keeps the mark but goes amber -- nothing would go out through it,
        // so it must not read the same as a healthy one.
        data.isPrimary &&
        <Icon icon="badge-check" type="solid" size={15}
          className={"ms-2 " + (data.isActive ? "text-primary" : "text-warning")}
          title={data.isActive ? "Primary instance" : "Primary instance (inactive)"} />
      }
    </FlexRow>
  )

  /** Row action, so promoting sits with edit/delete instead of loose in the Role column. */
  const MakePrimaryAction = (data: WaInstance) => {
    // Nothing to promote on the row that already holds it, and an instance that is switched
    // off would become a primary nothing can send through.
    if (data.isPrimary || !data.isActive) return <></>;
    return (
      <div className="menu-item">
        <a href="#" className="menu-link px-3"
          onClick={e => {
            e.preventDefault();
            selectedItemSet(data);
            primaryModalSet(true);
          }}>
          <Icon icon="star" className="me-2" />
          Make Primary
        </a>
      </div>
    )
  }

  const InstanceCol = (data: WaInstance) => (
    <div>
      <FlexRow>
        <div className="fs-7 fw-bold" style={{ letterSpacing: 1 }}>
          {data.idInstance}
        </div>
        {
          // Which instance the service is on right now, as opposed to which one is preferred.
          // The two differ exactly when the primary is down and a backup has taken over, which
          // is the moment it matters most that the list says so.
          data.isCurrent &&
          <span className="badge badge-success ms-3" title="The service is using this instance">
            In Use
          </span>
        }
      </FlexRow>
      <div className="fs-8 text-gray-600 mt-1">
        {data.phone?.phoneFriendly}
      </div>
    </div>
  )

  const UrlsCol = (data: WaInstance) => (
    <div className="fs-8 text-gray-700">
      <div>{data.apiUrl}</div>
      <div className="text-gray-600 mt-1">{data.mediaUrl}</div>
    </div>
  )

  return (
    <>
      <DataTable objectName="Instance"
        addText="New Instance"
        apiRoute={API_ROUTES.WaInstances}
        ascending
        refresh={refresh}
        edit
        delete
        addCustom={() => {
          selectedIdSet(undefined);
          formDrawerSet(true);
        }}
        editCustom={(data: WaInstance) => {
          selectedIdSet(data.id);
          formDrawerSet(true);
        }}>

        <DataTableField title="" width={50}>
          <DataTableActiveToggle route={API_ROUTES.WaInstances} callback={() => refreshSet(true)} />
        </DataTableField>

        <DataTableField title="Name">
          <NameCol />
        </DataTableField>

        <DataTableField title="Instance Id / Number">
          <InstanceCol />
        </DataTableField>

        <DataTableField title="Green API Status">
          <WaInstanceStateCell />
        </DataTableField>

        <DataTableField title="Endpoints">
          <UrlsCol />
        </DataTableField>

        <DataTableCustomActions>
          <MakePrimaryAction />
        </DataTableCustomActions>

      </DataTable>

      {
        formDrawer &&
        <WaInstanceForm instanceId={selectedId}
          onSave={() => refreshSet(true)}
          onClose={() => {
            selectedIdSet(undefined);
            formDrawerSet(false);
          }} />
      }

      {
        primaryModal && selectedItem &&
        <Modal id="modal-wainstance-primary"
          title="Make Primary"
          buttonText="Make Primary"
          open visible
          onClose={() => {
            primaryModalSet(false);
            selectedItemSet(undefined);
          }}
          onConfirm={callback => {
            hub.Post(API_ROUTES.WaInstances + "setprimary", {
              id: selectedItem.id
            }, res => {
              if (res.success) toastSuccess(res.message ?? "Primary instance has been changed!");
              else toastError(res.message);
              callback();
              refreshSet(true);
            })
          }}>
          <p className="ps-1">
            Make <strong>{selectedItem.name}</strong> the primary WhatsApp instance? The current
            primary becomes a backup.
          </p>
        </Modal>
      }

    </>
  );
}
